import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook para actividades CRM del usuario actual.
 * Filtra por responsable = currentUserId y categoriza en:
 *   - hoy: fecha === hoy y estado === 'pendiente'
 *   - proximas: fecha > hoy y estado === 'pendiente'
 *   - vencidas: fecha < hoy y estado === 'pendiente'
 */
export const useCRMActivities = (companyId, currentUserId) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const today = new Date()?.toISOString()?.split('T')?.[0];

  const fetchActivities = useCallback(async () => {
    if (!companyId || !currentUserId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('crm_activities')?.select(`
          *,
          contact:crm_contacts(id, nombre, telefono, whatsapp, email, empresa)
        `)?.eq('company_id', companyId)?.eq('responsable', currentUserId)?.eq('estado', 'pendiente')?.order('fecha', { ascending: true })?.order('hora', { ascending: true });

      if (fetchError) {
        console.error('Error fetching CRM activities:', fetchError);
        setError(fetchError?.message);
        setActivities([]);
        return;
      }
      setActivities(data || []);
    } catch (err) {
      console.error('useCRMActivities error:', err);
      setError(err?.message);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUserId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Realtime subscription
  useEffect(() => {
    if (!companyId || !currentUserId) return;

    const channel = supabase?.channel(`crm_activities_user_${currentUserId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_activities',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          fetchActivities();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [companyId, currentUserId, fetchActivities]);

  const hoy = activities?.filter(a => a?.fecha === today);
  const proximas = activities?.filter(a => a?.fecha > today);
  const vencidas = activities?.filter(a => a?.fecha < today);

  const totalPendientes = activities?.length;
  const totalHoy = hoy?.length;
  const totalVencidas = vencidas?.length;

  return {
    activities,
    hoy,
    proximas,
    vencidas,
    totalPendientes,
    totalHoy,
    totalVencidas,
    loading,
    error,
    refetch: fetchActivities,
  };
};
